import ChevronRightIcon from '@mui/icons-material/ChevronRight';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import FileCopyOutlinedIcon from '@mui/icons-material/FileCopyOutlined';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import Card from '@mui/material/Card';
import CardContent from '@mui/material/CardContent';
import CardHeader from '@mui/material/CardHeader';
import Collapse from '@mui/material/Collapse';
import IconButton from '@mui/material/IconButton';
import InputAdornment from '@mui/material/InputAdornment';
import Link from '@mui/material/Link';
import TextField from '@mui/material/TextField';
import Typography from '@mui/material/Typography';
import { DateTime } from 'luxon';
import { List } from 'immutable';
import React, { memo, useCallback, useMemo, useState } from 'react';
import { makeStyles } from 'tss-react/mui';

import {
  executeTransactionActionRequest,
  isCleanupTransactionProposalsResult,
  type CleanupTransactionSuggestion,
  type CleanupTransactionSuggestionField,
} from 'app-components/flux/ai-chat/transaction-actions';
import { chartOfAccountsTypes } from 'app-components/flux/chart-of-accounts';
import { featureFlagsSelectors } from 'app-components/flux/feature-flags';
import { tagsSelectors } from 'app-components/flux/tags';
import type { Attachment, QcsColor, TransactionProps } from 'app-components/flux/transactions/transactionsTypes';
import { tracker } from 'app-components/utils/core';
import type RootState from 'app-components/utils/redux-store/rootState';

import AttachmentsField from 'components/AttachmentsField';
import ClientProjectSelect from 'components/ClientProjectSelect';
import { useConnectionLost } from 'components/ConnectionStatus';
import QAmountField from 'components/QAmountField';
import QPanelButton from 'components/QPanelButton';
import SPayeeField from 'components/SPayeeField';
import AccountField from 'components/QuickenControls/AccountField';
import QTip from 'components/QuickenControls/QTip';
import BoxField from 'components/Transactions/BoxField';
import DateField from 'components/Transactions/DateField';
import { OtherSideField } from 'components/Transactions/OtherSideField';
import RecurringLinkRow from 'components/Transactions/RecurringLinkRow';
import RefundLine from 'components/Transactions/RefundLine';
import ReceiptSection from 'components/Transactions/ReceiptSection';
import StatusField from 'components/Transactions/StatusField';
import { dispatchSimpleNotification } from 'data/notifications/notificationsUtils';
import { useAppSelector, useAppStore } from 'hooks/redux';

import { withDrawerWidth, type RootModalDrawerOptions } from 'components/Drawers/standardDrawerContract';
import { useStyles as useSharedStyles } from '../styles';
import { AI_HIGHLIGHT_RAW_INPUT_SX, AI_HIGHLIGHT_TEXTFIELD_SX } from './aiHighlightSx';
import CardPaymentStatementNote from './CardPaymentStatementNote';
import CategorySection from './CategorySection';
import type { TransactionEditViewModel } from './TransactionEditViewModel';
import FastActionsRow from './FastActionsRow';

const useStyles = makeStyles()((theme) => ({
  fieldGroup: {
    display: 'flex',
    flexDirection: 'column',
    gap: theme.spacing(2),
    width: '100%',
  },
  fullField: {
    width: '100%',
    margin: 0,
  },
  dateStatusRow: {
    display: 'flex',
    gap: theme.spacing(2),
    width: '100%',
    '& .MuiFormControl-root': {
      margin: 0,
      flex: 1,
    },
  },
  splitAmountButton: {
    margin: 0,
    padding: 0,
    height: 58,
    width: '100%',
    borderRadius: 12,
    border: `1px solid ${theme.palette.border.light}`,
    boxShadow: '0px -1px 2px 0px rgba(0, 0, 0, 0.05), 0px 2px 4px 0px rgba(0, 0, 0, 0.16)',
    backgroundColor: theme.palette.background.paper,
  },
  matchRow: {
    display: 'flex',
    justifyContent: 'flex-start',
    width: '100%',
  },
  deleteRow: {
    display: 'flex',
    justifyContent: 'center',
    width: '100%',
  },
  aiActionButton: {
    width: '100%',
  },
}));

// Snapshot of the field values right before suggestions were applied, keyed by the view-model
// field they came from (not the suggestion's own `currentValue`, which for 'category' is only a
// display name - not the category id needed to actually revert it via setCoa).
type CleanupTransactionPreviousValues = {
  payee?: string;
  memo?: string;
  coa?: chartOfAccountsTypes.ChartOfAccountProps | null;
  tags?: TransactionProps['tags'];
  userFlag?: QcsColor | null;
};

type CleanupTransactionState =
  | { status: 'idle' }
  | { status: 'loading' }
  | {
      status: 'applied';
      appliedSuggestions: CleanupTransactionSuggestion[];
      previousValues: CleanupTransactionPreviousValues;
    }
  | { status: 'no_suggestions' }
  | { status: 'error'; message: string };

const CLEANUP_TRANSACTION_FIELD_LABELS: Record<CleanupTransactionSuggestion['field'], string> = {
  payee: 'Payee',
  category: 'Category',
  memo: 'Note',
  tags: 'Tags',
  user_flag: 'Flag',
};

const formatCleanupTransactionValue = (value: string | string[]): string => {
  if (Array.isArray(value)) {
    return value.length > 0 ? value.join(', ') : '(none)';
  }
  return value.trim() ? value : '(none)';
};

type Props = {
  viewModel: TransactionEditViewModel;
  rootModalDrawer?: RootModalDrawerOptions;
};

/**
 * Redesigned single-column transaction-detail layout used when the dialog is presented as a
 * side drawer. Pure presentation re-composition of the existing view-model — every field, action,
 * and gating rule is unchanged from the modal layout; only the hierarchy/spacing differs.
 */
function TransactionEditDrawerBody({ viewModel, rootModalDrawer }: Props): React.JSX.Element {
  const { classes: shared } = useSharedStyles();
  const { classes } = useStyles();
  const store = useAppStore();
  const aiTransactionActionsEnabled = useAppSelector((state) =>
    featureFlagsSelectors.getFeatureFlag(state, 'aiTransactionActions'),
  );
  // The drawer footer hides Update while offline; Delete lives in the body, so it needs its own gate.
  const connectionLost = useConnectionLost();
  const [cleanupTransactionState, setCleanupTransactionState] = useState<CleanupTransactionState>({
    status: 'idle',
  });
  const [isCleanupSummaryExpanded, setIsCleanupSummaryExpanded] = useState(true);

  const {
    actions: {
      closeAll,
      disabledPayment,
      goToOtherSide,
      markRecurring,
      onDelete,
      onSetEditSeries,
      onViewRecurring,
      openReceiptSplits,
      openSplitDialog,
      resetCustomersAndProjects,
      selectAccount,
      unMarkAsBill,
      unMatchTransaction,
      unlinkRecurring,
    },
    derived: {
      allAccounts,
      businessEnabled,
      coa,
      completeEnough,
      currentSplitPayments,
      currentTxn,
      date,
      dateError,
      dateInfoText,
      deleteQTip,
      disableReportsValue,
      disableSalesReceiptFields,
      focusSplits,
      fullDisableQBP,
      futureTxnsFf,
      hasSplitItemsDefined,
      hasSplitSeries,
      hasSplitTransferCounterpart,
      hideClientsAndProjects,
      isAccountClosed,
      isAssetPayment,
      isIncome,
      isManual,
      isMatched,
      linkedExpense,
      model,
      openAccounts,
      ornamentType,
      paymentId,
      refundTracker,
      receiptId,
      showCheckNumberField,
      showMatchOrTransferRow,
      showPaymentDialogLink,
      split,
      splitSeriesFf,
      splitsDisabled,
      supportedTransfer,
      txn,
      validPayee,
      visibilityExcludeReportsDisplay,
      visibilityF2SDisplay,
    },
    setters: {
      setAttachments,
      setBusinessId,
      setCheckNumber,
      setCoa,
      setCustomersAndProjects,
      setDate,
      setDateError,
      setExcludeF2S,
      setExcludeReports,
      setFlag,
      setMemo,
      setPayee,
      setStatus,
      setTags,
      updateAmount,
      updateReviewed,
    },
    values: {
      accountId,
      amount,
      attachments,
      businessId,
      checkNumber,
      create,
      customersAndProjects,
      isBill,
      isReviewed,
      memo,
      payee,
      status,
      userFlag,
    },
  } = viewModel;

  const allowStatus = create || !fullDisableQBP || status !== 'CLEARED' || txn.subtype === 'SALES_RECEIPT';
  const showSplitAmount = Boolean((split?.items || hasSplitSeries) && !hasSplitTransferCounterpart);
  const detailFieldsDisabled = Boolean(showPaymentDialogLink || linkedExpense || disableSalesReceiptFields);
  const currentTransactionId = currentTxn.id;
  const canUseAiTransactionActions = Boolean(aiTransactionActionsEnabled && currentTransactionId);
  // Lock the fields a cleanup suggestion can touch while the request is in flight, so an edit
  // made mid-request can't get silently overwritten once the response lands and applies.
  const isCleanupTransactionLoading = cleanupTransactionState.status === 'loading';

  // Only covers the non-'applied' statuses - 'applied' renders a detailed per-field
  // before/after list instead of a single summary line (see the render below).
  const cleanupTransactionStatusText = useMemo(() => {
    switch (cleanupTransactionState.status) {
      case 'loading':
        return 'Looking for cleanup suggestions...';
      case 'no_suggestions':
        return 'No cleanup suggestions right now — this transaction already looks good.';
      case 'error':
        return cleanupTransactionState.message;
      default:
        return undefined;
    }
  }, [cleanupTransactionState]);

  // Which fields to visually highlight (green outline) right where they live in the form,
  // in addition to the detailed summary list above.
  const cleanupHighlightedFields = useMemo(
    () =>
      cleanupTransactionState.status === 'applied'
        ? new Set(cleanupTransactionState.appliedSuggestions.map((suggestion) => suggestion.field))
        : new Set<CleanupTransactionSuggestion['field']>(),
    [cleanupTransactionState],
  );

  const handleCleanupTransaction = useCallback(() => {
    // The button is the only caller today and is already disabled while loading/applied, but
    // guard here too rather than relying solely on that - a re-entrant call would otherwise start
    // a second request off the already-modified draft (see the button's `disabled` comment above).
    if (!currentTransactionId || cleanupTransactionState.status === 'loading') {
      return;
    }

    setCleanupTransactionState({ status: 'loading' });
    const state = store.getState() as RootState;

    executeTransactionActionRequest(state, {
      capability: 'cleanup_transaction',
      transactionIds: [currentTransactionId],
      source: 'transaction-details',
      // currentTxn is this drawer's local draft (edits aren't written to Redux until Save), so
      // pass it directly rather than letting the framework read the stale persisted transaction.
      transactionOverrides: { [currentTransactionId]: currentTxn },
    })
      .then((result) => {
        if (result.status === 'request_failed') {
          setCleanupTransactionState({ status: 'error', message: result.error });
          return;
        }

        if (!isCleanupTransactionProposalsResult(result) || result.proposals.length === 0) {
          setCleanupTransactionState({ status: 'no_suggestions' });
          return;
        }

        const [proposal] = result.proposals;
        const appliedSuggestions: CleanupTransactionSuggestion[] = [];
        const previousValues: CleanupTransactionPreviousValues = {};

        proposal.suggestions.forEach((suggestion) => {
          switch (suggestion.field) {
            case 'payee':
              previousValues.payee = payee;
              setPayee(suggestion.proposedValue as string);
              break;
            case 'memo':
              previousValues.memo = memo;
              setMemo(suggestion.proposedValue as string);
              break;
            case 'category':
              if (!suggestion.proposedCategoryId) return;
              previousValues.coa = coa;
              setCoa({ type: chartOfAccountsTypes.CoaTypeEnum.CATEGORY, id: suggestion.proposedCategoryId });
              break;
            case 'tags': {
              previousValues.tags = currentTxn.tags;
              const resolvedTags = (suggestion.proposedValue as string[])
                .map((name) => tagsSelectors.getTagByName(state, name))
                .filter((tag): tag is NonNullable<typeof tag> => !!tag);
              setTags(List(resolvedTags));
              break;
            }
            case 'user_flag':
              previousValues.userFlag = userFlag;
              setFlag(suggestion.proposedValue as QcsColor);
              break;
            default:
              return;
          }
          appliedSuggestions.push(suggestion);
        });

        if (appliedSuggestions.length === 0) {
          setCleanupTransactionState({ status: 'no_suggestions' });
          return;
        }

        setIsCleanupSummaryExpanded(true);
        setCleanupTransactionState({ status: 'applied', appliedSuggestions, previousValues });
      })
      .catch(() => {
        setCleanupTransactionState({
          status: 'error',
          message: 'We could not clean up this transaction right now. Please try again.',
        });
      });
  }, [
    currentTransactionId,
    cleanupTransactionState.status,
    payee,
    memo,
    coa,
    currentTxn,
    userFlag,
    setPayee,
    setMemo,
    setCoa,
    setTags,
    setFlag,
    store,
  ]);

  const revertCleanupField = useCallback(
    (field: CleanupTransactionSuggestionField, previousValues: CleanupTransactionPreviousValues) => {
      switch (field) {
        case 'payee':
          if (previousValues.payee !== undefined) setPayee(previousValues.payee);
          break;
        case 'memo':
          if (previousValues.memo !== undefined) setMemo(previousValues.memo);
          break;
        case 'category':
          setCoa(previousValues.coa);
          break;
        case 'tags':
          setTags(previousValues.tags);
          break;
        case 'user_flag':
          setFlag(previousValues.userFlag);
          break;
        default:
          break;
      }
    },
    [setPayee, setMemo, setCoa, setTags, setFlag],
  );

  // Fields stay editable once a suggestion is applied, so by the time Undo/Revert all runs the
  // user may have already typed over what the cleanup pass set. Only restore the pre-AI snapshot
  // for a field if it still holds exactly the value that was applied - otherwise reverting would
  // silently clobber the user's own newer edit.
  const isCleanupFieldStillAtAppliedValue = useCallback(
    (suggestion: CleanupTransactionSuggestion): boolean => {
      switch (suggestion.field) {
        case 'payee':
          return payee === suggestion.proposedValue;
        case 'memo':
          return memo === suggestion.proposedValue;
        case 'category':
          return coa?.id === suggestion.proposedCategoryId;
        case 'tags': {
          const proposedNames = new Set(suggestion.proposedValue as string[]);
          const currentNames = new Set((currentTxn.tags ?? []).map((tag) => tag.name));
          return proposedNames.size === currentNames.size && [...proposedNames].every((name) => currentNames.has(name));
        }
        case 'user_flag':
          return userFlag === suggestion.proposedValue;
        default:
          return false;
      }
    },
    [payee, memo, coa, currentTxn.tags, userFlag],
  );

  const handleRevertCleanupField = useCallback(
    (field: CleanupTransactionSuggestionField) => {
      if (cleanupTransactionState.status !== 'applied') return;

      const suggestion = cleanupTransactionState.appliedSuggestions.find((s) => s.field === field);
      if (suggestion && isCleanupFieldStillAtAppliedValue(suggestion)) {
        revertCleanupField(field, cleanupTransactionState.previousValues);
      }

      const remainingSuggestions = cleanupTransactionState.appliedSuggestions.filter(
        (suggestion) => suggestion.field !== field,
      );
      setCleanupTransactionState(
        remainingSuggestions.length > 0
          ? {
              status: 'applied',
              appliedSuggestions: remainingSuggestions,
              previousValues: cleanupTransactionState.previousValues,
            }
          : { status: 'idle' },
      );
    },
    [cleanupTransactionState, revertCleanupField, isCleanupFieldStillAtAppliedValue],
  );

  const handleRevertAllCleanup = useCallback(() => {
    if (cleanupTransactionState.status !== 'applied') return;

    cleanupTransactionState.appliedSuggestions.forEach((suggestion) => {
      if (isCleanupFieldStillAtAppliedValue(suggestion)) {
        revertCleanupField(suggestion.field, cleanupTransactionState.previousValues);
      }
    });
    setCleanupTransactionState({ status: 'idle' });
  }, [cleanupTransactionState, revertCleanupField, isCleanupFieldStillAtAppliedValue]);

  // Transfers are a kind of category, so the transfer "other side" link renders right below the
  // category (between category and tags) via CategorySection. The merge/unmerge note stays at the
  // bottom of the drawer.
  const transferRow =
    showMatchOrTransferRow && supportedTransfer ? (
      <div className={classes.matchRow}>
        <Typography variant='captionRegular'>
          <OtherSideField
            coa={txn.coa}
            transferId={txn.transfer?.id}
            txnId={txn.id ?? txn.clientId}
            goToOtherSide={goToOtherSide}
          />
        </Typography>
      </div>
    ) : null;

  return (
    <>
      <CardPaymentStatementNote splitSeriesFf={splitSeriesFf} txn={txn} />

      {/* 1. Identity group */}
      <div className={classes.fieldGroup}>
        {canUseAiTransactionActions ? (
          <>
            <Button
              id='cleanup-transaction-with-ai'
              variant='outlined'
              className={classes.aiActionButton}
              onClick={handleCleanupTransaction}
              // Once suggestions are applied, re-running would build the next request off the
              // already-modified draft and overwrite `previousValues`, making "Revert" only
              // restore to the post-first-run state instead of the true original. Disabled until
              // the user reverts (which drops status back to 'idle') rather than allowing a stack
              // of runs on top of each other.
              disabled={isCleanupTransactionLoading || fullDisableQBP || cleanupTransactionState.status === 'applied'}
            >
              {isCleanupTransactionLoading ? 'Cleaning up transaction...' : 'Clean up transaction with AI'}
            </Button>
            {cleanupTransactionState.status === 'applied' ? (
              <Card variant='outlined' sx={{ width: '100%' }}>
                <CardHeader
                  onClick={() => setIsCleanupSummaryExpanded((expanded) => !expanded)}
                  sx={{
                    cursor: 'pointer',
                    '& .MuiCardHeader-action': { alignSelf: 'center', display: 'flex', alignItems: 'center', m: 0 },
                  }}
                  title={<Typography variant='paragraphMedium'>AI updated the following:</Typography>}
                  action={
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                      <Link
                        component='button'
                        type='button'
                        variant='captionRegular'
                        onClick={(event) => {
                          event.stopPropagation();
                          handleRevertAllCleanup();
                        }}
                      >
                        Revert all
                      </Link>
                      <ChevronRightIcon
                        fontSize='small'
                        sx={{
                          transform: isCleanupSummaryExpanded ? 'rotate(90deg)' : 'none',
                          transition: (theme) =>
                            theme.transitions.create('transform', { duration: theme.transitions.duration.shortest }),
                        }}
                      />
                    </Box>
                  }
                />
                <Collapse in={isCleanupSummaryExpanded} timeout='auto' unmountOnExit>
                  <CardContent sx={{ display: 'flex', flexDirection: 'column', gap: 0.5, pt: 0 }}>
                    {cleanupTransactionState.appliedSuggestions.map((suggestion) => (
                      <Box
                        key={suggestion.field}
                        sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 1 }}
                      >
                        <Typography variant='captionRegular' color='text.secondary'>
                          <Typography component='span' variant='captionBold' color='text.primary'>
                            {CLEANUP_TRANSACTION_FIELD_LABELS[suggestion.field]}
                          </Typography>
                          {': '}
                          {formatCleanupTransactionValue(suggestion.currentValue)} →{' '}
                          <Typography component='span' variant='captionMedium' color='text.primary'>
                            {formatCleanupTransactionValue(suggestion.proposedValue)}
                          </Typography>
                        </Typography>
                        <Link
                          component='button'
                          type='button'
                          variant='captionRegular'
                          onClick={() => handleRevertCleanupField(suggestion.field)}
                        >
                          Undo
                        </Link>
                      </Box>
                    ))}
                  </CardContent>
                </Collapse>
              </Card>
            ) : cleanupTransactionStatusText ? (
              <Typography variant='captionRegular' color='text.secondary'>
                {cleanupTransactionStatusText}
              </Typography>
            ) : null}
          </>
        ) : null}

        <SPayeeField
          id='payee-field-editable'
          defaultValue={payee}
          onChange={setPayee}
          className={classes.fullField}
          disabled={isCleanupTransactionLoading}
          // Drop the field's default MUI 'normal' margin (16px top / 8px bottom); the drawer's
          // fieldGroup gap governs spacing, matching the other identity fields.
          textFieldProps={{
            margin: 'none',
            sx: cleanupHighlightedFields.has('payee') ? AI_HIGHLIGHT_TEXTFIELD_SX : undefined,
          }}
          popupIcon={null}
          autoFocus={!focusSplits}
        />

        {showSplitAmount ? (
          <QPanelButton
            id='edit-split-amount'
            disabled={linkedExpense || (txn.type === 'PAYMENT' && isAssetPayment) || txn.subtype === 'SALES_RECEIPT'}
            className={classes.splitAmountButton}
            onClick={() => {
              openSplitDialog();
              tracker.track(tracker.events.viewSplitsV2, {
                initiator: txn.id === null ? 'createTxn' : 'editTxnWithSplits',
                transactionAmount: amount,
                transactionAmountSign: amount >= 0 ? 'positive' : 'negative',
              });
            }}
            title={
              <QAmountField
                label='Amount'
                value={amount}
                showSign='positive'
                showColor='positive'
                inputId='details-amount'
              />
            }
            arrow={
              <IconButton size='small'>
                <ChevronRightIcon fontSize='small' />
              </IconButton>
            }
          />
        ) : (
          <QAmountField
            label='Amount'
            margin='none'
            className={classes.fullField}
            fullWidth
            textFieldVariant='filled'
            editable
            submitOnBlur
            value={amount}
            onChange={updateAmount}
            onSubmit={updateAmount}
            expectedSign={isIncome ? 'positive' : 'negative'}
            id='details-amount'
            disabled={fullDisableQBP || hasSplitTransferCounterpart}
          />
        )}

        <div className={classes.dateStatusRow}>
          <DateField
            date={date}
            onChange={setDate}
            onError={(error: string | undefined) => setDateError(error)}
            maxDate={futureTxnsFf ? DateTime.now().plus({ year: 1 }) : undefined}
            variant='filled'
            allowInvalid
            error={!!dateError}
            helperText={dateError || dateInfoText || ''}
            disabled={fullDisableQBP || hasSplitTransferCounterpart}
          />
          <StatusField status={status} onChange={setStatus} readOnly={!allowStatus} margin='none' variant='filled' />
        </div>

        <AccountField
          initialAccountId={accountId}
          accounts={showPaymentDialogLink || txn.type === 'PAYMENT' ? allAccounts : openAccounts}
          onSelected={selectAccount}
          label='Account'
          variant='filled'
          margin='none'
          className={classes.fullField}
          fullWidth
          readOnly={!isManual || hasSplitTransferCounterpart}
          id='account-select'
          disabled={fullDisableQBP || hasSplitTransferCounterpart}
        />
        {txn.paymentTxnType ? (
          <TextField id='payment-type-field' label='Type' variant='filled' value={txn.paymentTxnType} disabled />
        ) : null}
      </div>

      <div className={shared.dividerSection}>
        <div className={shared.divider} />
      </div>

      {/* 2. Fast actions: flag, exclusions, reviewed */}
      <FastActionsRow
        txnId={txn.id}
        userFlag={userFlag}
        setFlag={setFlag}
        isReviewed={isReviewed}
        updateReviewed={updateReviewed}
        disableReportsValue={disableReportsValue}
        visibilityF2SDisplay={visibilityF2SDisplay}
        visibilityExcludeReportsDisplay={visibilityExcludeReportsDisplay}
        setExcludeF2S={setExcludeF2S}
        setExcludeReports={setExcludeReports}
        managedPerSplit={hasSplitItemsDefined}
        flagHighlighted={cleanupHighlightedFields.has('user_flag')}
        flagDisabled={isCleanupTransactionLoading}
      />

      {receiptId ? (
        <ReceiptSection receiptId={receiptId} hasSplits={hasSplitItemsDefined} onCreateSplits={openReceiptSplits} />
      ) : null}

      <div className={shared.dividerSection}>
        <div className={shared.divider} />
      </div>

      {/* 3 & 4. Category/Tags/Split + recurring, grouped so they share the 16px field spacing
          rather than the drawer's 24px inter-section gap. */}
      <div className={classes.fieldGroup}>
        <CategorySection
          drawerLayout
          accountId={accountId}
          coa={coa}
          setCoa={setCoa}
          setTags={setTags}
          amount={amount}
          businessId={businessId}
          setBusinessId={setBusinessId}
          customersAndProjects={customersAndProjects}
          resetCustomersAndProjects={resetCustomersAndProjects}
          validPayee={validPayee}
          onOpenSplitDialog={openSplitDialog}
          txn={currentTxn}
          belowCategoryContent={transferRow}
          showSplitRow={!receiptId && refundTracker && !hasSplitTransferCounterpart}
          splitsDisabled={splitsDisabled || !!disableSalesReceiptFields || hasSplitTransferCounterpart}
          disabled={Boolean(
            showPaymentDialogLink ||
              linkedExpense ||
              disableSalesReceiptFields ||
              hasSplitTransferCounterpart ||
              isCleanupTransactionLoading,
          )}
          tagsDisabled={Boolean(
            showPaymentDialogLink || linkedExpense || disableSalesReceiptFields || isCleanupTransactionLoading,
          )}
          goToOtherSide={goToOtherSide}
          disabledPayment={disabledPayment(businessId)}
          categoryHighlighted={cleanupHighlightedFields.has('category')}
          tagsHighlighted={cleanupHighlightedFields.has('tags')}
        />

        {/* Recurring / one-time bill / series */}
        <RecurringLinkRow
          variant='block'
          txn={txn}
          model={model}
          isBill={isBill}
          unMarkAsBill={unMarkAsBill}
          markRecurring={markRecurring}
          viewRecurring={onViewRecurring}
          disabledRecurring={!completeEnough || isAccountClosed || fullDisableQBP}
          setEditSeries={onSetEditSeries}
          unlinkRecurring={unlinkRecurring}
        />
      </div>

      <div className={shared.dividerSection}>
        <div className={shared.divider} />
      </div>

      {/* 5 & 6. Clients & Projects + notes/attachments/check#/payment id — one 16px field group. */}
      <div className={classes.fieldGroup}>
        {businessEnabled && !hideClientsAndProjects ? (
          <ClientProjectSelect
            value={customersAndProjects}
            onChange={setCustomersAndProjects}
            className={shared.clientProjectSelect}
            ornamentType={ornamentType}
            disabledInvoicePayment={disabledPayment(businessId) || disableSalesReceiptFields}
            disabledSelection={
              showPaymentDialogLink ||
              linkedExpense ||
              currentSplitPayments > 0 ||
              hasSplitTransferCounterpart ||
              disableSalesReceiptFields
            }
            invoiceId={currentTxn?.linkedItem?.id}
            paymentAssetId={currentTxn?.id}
            onButtonClick={closeAll}
          />
        ) : null}

        {paymentId ? (
          <TextField
            id='payment-id-field'
            label='Payment ID'
            value={paymentId}
            className={shared.paymentId}
            InputProps={{
              readOnly: true,
              endAdornment: (
                <InputAdornment position='end'>
                  <IconButton
                    size='small'
                    onClick={() => {
                      navigator.clipboard.writeText(paymentId);
                      dispatchSimpleNotification('Payment ID copied to clipboard');
                    }}
                  >
                    <FileCopyOutlinedIcon fontSize='inherit' />
                  </IconButton>
                </InputAdornment>
              ),
            }}
          />
        ) : null}
        <BoxField
          id='memo-field'
          label='Note'
          placeholder='Add your note here'
          value={memo}
          onChange={setMemo}
          inputProps={{ maxLength: 512 }}
          margin='none'
          disabled={isCleanupTransactionLoading}
          sx={cleanupHighlightedFields.has('memo') ? AI_HIGHLIGHT_RAW_INPUT_SX : undefined}
        />
        <AttachmentsField
          id='attachments-field'
          attachments={attachments}
          onChange={(_event: unknown, newAttachments: Attachment[]) => setAttachments(newAttachments)}
          box
          margin='none'
        />
        {showCheckNumberField ? (
          <TextField
            label='Check #'
            value={checkNumber}
            onChange={(event) => setCheckNumber(event.target.value)}
            variant='filled'
          />
        ) : null}
      </div>

      {/* 7. Refund tracking — RefundLine renders null when not applicable, so it's not wrapped in a
          div (an always-present empty wrapper would add a phantom flex gap). The details drawer is
          narrow ('Small'), but the refund sub-flow it launches uses the roomier 'Large' width like
          the other sub-flows launched from details. */}
      {refundTracker && !hasSplitTransferCounterpart && (
        <RefundLine
          txn={currentTxn}
          disabled={detailFieldsDisabled}
          rootModalDrawer={withDrawerWidth(rootModalDrawer, 'Large')}
          leftAlign
        />
      )}

      {/* Merge/unmerge note stays at the bottom (transfers move up under the category). */}
      {showMatchOrTransferRow && isMatched && (
        <div className={classes.matchRow}>
          <Typography variant='captionRegular'>
            {txn.source === 'USER_ENTERED'
              ? 'A manual and bank transaction were combined to create this entry. '
              : 'A pending and cleared transaction were combined to create this entry. '}
            <Link className={shared.link} onClick={() => unMatchTransaction(txn)} id='unmerge-button'>
              Unmerge
            </Link>
          </Typography>
        </div>
      )}

      {/* 8. Delete */}
      {!create && (
        <div className={classes.deleteRow}>
          <QTip title={deleteQTip}>
            <span>
              <Button
                id='delete-txn'
                aria-label='delete-txn'
                onClick={onDelete}
                disabled={fullDisableQBP || connectionLost}
                className={shared.deleteButton}
                startIcon={<DeleteOutlineIcon />}
              >
                Delete transaction
              </Button>
            </span>
          </QTip>
        </div>
      )}
    </>
  );
}

export default memo(TransactionEditDrawerBody);
